'use client'

import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, Plus, Trash2 } from 'lucide-react'
import { addDoc, collection, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Checkbox } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Separator } from 'components/ui/separator'
import { Textarea } from 'components/ui/textarea'
import { toast } from 'sonner'
import { auth, db, storage } from '../../../../firebase'
import { useUser } from 'components/context/user-context'

const steps = ['Basic Info', 'Budget', 'SDG Mapping', 'Documents', 'Review & Submit']

const SDG_LIST = [
  'No Poverty',
  'Zero Hunger',
  'Good Health & Well-being',
  'Quality Education',
  'Gender Equality',
  'Clean Water & Sanitation',
  'Affordable & Clean Energy',
  'Decent Work & Growth',
  'Industry & Innovation',
  'Reduced Inequalities',
  'Sustainable Cities',
  'Responsible Consumption',
  'Climate Action',
  'Life Below Water',
  'Life on Land',
  'Peace & Justice',
  'Partnerships',
]

const defaultBudgetItems = [
  { id: 'new-1', category: 'Personnel', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-2', category: 'Equipment', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-3', category: 'Travel', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-4', category: 'Training', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-5', category: 'Overheads', description: '', quantity: 1, unitCost: 0, total: 0 },
]

function formatCurrency(value, currency) {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency || 'KES',
    maximumFractionDigits: 0,
  }).format(amount)
}

export default function NewProposalPage() {
  const navigate = useNavigate()
  const { canEdit } = useUser()
  const fileInputRef = React.useRef(null)
  const [step, setStep] = React.useState(0)
  const [selectedFiles, setSelectedFiles] = React.useState([])
  const [submitting, setSubmitting] = React.useState(false)
  const [availableDepartments, setAvailableDepartments] = React.useState([])
  const [stakeholders, setStakeholders] = React.useState([])
  const [formData, setFormData] = React.useState({
    title: '',
    type: 'donor_funded',
    department: '',
    description: '',
    donorName: '',
    donorId: '',
    startDate: '',
    endDate: '',
    currency: 'KES',
    sdgMappings: [],
    strategicPillars: [],
    conflictDeclaration: false,
    documentUrls: [],
    budgetItems: defaultBudgetItems,
  })
  const departmentCollectionName = 'Departments'

  React.useEffect(() => {
    let isMounted = true

    async function loadDepartments() {
      try {
        const snapshot = await getDocs(collection(db, departmentCollectionName))
        if (!isMounted) return
        const departments = snapshot.docs
          .map((docItem) => docItem.data()?.schools)
          .filter(Boolean)
        setAvailableDepartments(Array.from(new Set(departments)))
      } catch {
        setAvailableDepartments([])
      }
    }

    loadDepartments()

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    let isMounted = true

    async function loadStakeholders() {
      try {
        const snapshot = await getDocs(collection(db, 'stakeholders'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setStakeholders(records)
      } catch {
        setStakeholders([])
      }
    }

    loadStakeholders()

    return () => {
      isMounted = false
    }
  }, [])

  const grandTotal = formData.budgetItems.reduce(
    (sum, item) => sum + item.quantity * item.unitCost,
    0,
  )

  const updateBudgetItem = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      budgetItems: prev.budgetItems.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
              total:
                field === 'quantity' || field === 'unitCost'
                  ? (field === 'quantity' ? Number(value) : item.quantity) *
                    (field === 'unitCost' ? Number(value) : item.unitCost)
                  : item.total,
            }
          : item,
      ),
    }))
  }

  const addBudgetLine = () => {
    setFormData((prev) => ({
      ...prev,
      budgetItems: [
        ...prev.budgetItems,
        { id: `new-${Date.now()}`, category: 'Other', description: '', quantity: 1, unitCost: 0, total: 0 },
      ],
    }))
  }

  const removeBudgetLine = (index) => {
    setFormData((prev) => ({
      ...prev,
      budgetItems: prev.budgetItems.filter((_, i) => i !== index),
    }))
  }

  const toggleSDG = (sdgNumber) => {
    setFormData((prev) => ({
      ...prev,
      sdgMappings: prev.sdgMappings.includes(sdgNumber)
        ? prev.sdgMappings.filter((value) => value !== sdgNumber)
        : [...prev.sdgMappings, sdgNumber],
    }))
  }

  const handleFilesSelected = (files) => {
    const incoming = Array.from(files || [])
    if (incoming.length === 0) return
    const maxSize = 25 * 1024 * 1024
    const validFiles = []
    let rejectedCount = 0

    incoming.forEach((file) => {
      if (file.size > maxSize) {
        rejectedCount += 1
        return
      }
      validFiles.push(file)
    })

    if (rejectedCount > 0) {
      toast.error('Some files exceeded 25MB and were skipped.')
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
      toast.success(`${validFiles.length} file${validFiles.length === 1 ? '' : 's'} added.`)
    }
  }

  const uploadDocuments = async (proposalId) => {
    if (selectedFiles.length === 0) return []
    const uploads = selectedFiles.map(async (file, index) => {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const uniqueSuffix = `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`
      const path = `proposals/${proposalId}/${uniqueSuffix}-${safeName}`
      const storageRef = ref(storage, path)
      await uploadBytes(storageRef, file, { contentType: file.type })
      const url = await getDownloadURL(storageRef)
      return {
        name: file.name,
        url,
        path,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
      }
    })
    return Promise.all(uploads)
  }

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a proposal title')
      return
    }
    if (submitting) return
    setSubmitting(true)

    const basePayload = {
      title: formData.title,
      type: formData.type,
      departmentId: formData.department || 'Not specified',
      description: formData.description,
      donorName: formData.donorName,
      donorId: formData.donorId || '',
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      budgetItems: formData.budgetItems.map((item) => ({
        ...item,
        total: item.quantity * item.unitCost,
      })),
      totalBudget: grandTotal,
      currency: formData.currency,
      sdgMappings: formData.sdgMappings,
      strategicPillars: formData.strategicPillars,
      conflictDeclaration: formData.conflictDeclaration,
    }

    try {
      const currentUser = auth.currentUser
      const proposalPayload = {
        ...basePayload,
        status: 'draft',
        submittedBy: currentUser?.uid ?? 'unknown',
        submittedByName: currentUser?.displayName ?? currentUser?.email ?? 'Unknown',
        documentUrls: [],
        reviewScores: [],
        statusHistory: [
          {
            status: 'draft',
            changedBy: currentUser?.uid ?? 'unknown',
            changedByName: currentUser?.displayName ?? currentUser?.email ?? 'Unknown',
            comment: 'Proposal created',
            timestamp: new Date(),
          },
        ],
      }

      const docRef = await addDoc(collection(db, 'proposals'), {
        ...proposalPayload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      const newlyUploaded = await uploadDocuments(docRef.id)
      if (newlyUploaded.length > 0) {
        await updateDoc(docRef, {
          documentUrls: newlyUploaded,
          updatedAt: serverTimestamp(),
        })
      }

      toast.success('Proposal submitted successfully!', {
        description: `"${formData.title}" has been saved as a draft.`,
      })
      setSelectedFiles([])
      navigate('/dashboard/proposals')
    } catch (error) {
      toast.error('Failed to submit proposal', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!canEdit) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <h2 className="text-lg font-semibold text-foreground">Read-only access</h2>
          <p className="text-sm text-muted-foreground mt-2">
            You do not have permission to create new proposals.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild className="size-8">
          <Link to="/dashboard/proposals">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            New Proposal
          </h1>
          <p className="text-sm text-muted-foreground">
            Create a new research proposal for internal review.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {steps.map((label, index) => {
          const isActive = index === step
          const isComplete = index < step
          return (
            <button
              key={label}
              onClick={() => setStep(index)}
              className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition ${
                isActive
                  ? 'border-primary text-primary'
                  : isComplete
                  ? 'border-primary/40 text-foreground'
                  : 'border-border text-muted-foreground'
              }`}
            >
              <span
                className={`flex size-6 items-center justify-center rounded-full text-[10px] font-semibold ${
                  isActive || isComplete
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {isComplete ? <Check className="size-3" /> : index + 1}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </button>
          )
        })}
      </div>

      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Basic Information</CardTitle>
            <CardDescription>Enter the core details of your research proposal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                placeholder="Enter project title"
                value={formData.title}
                onChange={(event) => setFormData({ ...formData, title: event.target.value })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Project Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="donor_funded">Donor Funded</SelectItem>
                    <SelectItem value="internal">Internal (University-Funded)</SelectItem>
                    <SelectItem value="collaborative">Collaborative / Consortium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department / School *</Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => setFormData({ ...formData, department: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDepartments.length === 0 ? (
                      <SelectItem value="no-departments" disabled>
                        No departments available
                      </SelectItem>
                    ) : (
                      availableDepartments.map((department) => (
                        <SelectItem key={department} value={department}>
                          {department}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.type === 'donor_funded' ? (
              <div className="space-y-2">
                <Label htmlFor="donor">Donor / Funding Agency</Label>
                <Select
                  value={formData.donorId}
                  onValueChange={(value) => {
                    const stakeholder = stakeholders.find((item) => item.id === value)
                    setFormData((prev) => ({
                      ...prev,
                      donorId: value,
                      donorName: stakeholder?.organization || stakeholder?.name || stakeholder?.fullName || prev.donorName,
                    }))
                  }}
                >
                  <SelectTrigger id="donor">
                    <SelectValue placeholder={stakeholders.length === 0 ? 'No stakeholders available' : 'Select stakeholder'} />
                  </SelectTrigger>
                  <SelectContent>
                    {stakeholders.length === 0 ? (
                      <SelectItem value="no-stakeholders" disabled>
                        No stakeholders found
                      </SelectItem>
                    ) : (
                      stakeholders.map((stakeholder) => (
                        <SelectItem key={stakeholder.id} value={stakeholder.id}>
                          {stakeholder.name || stakeholder.fullName || 'Stakeholder'}
                          {stakeholder.organization ? ` • ${stakeholder.organization}` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="desc">Description *</Label>
              <Textarea
                id="desc"
                placeholder="Brief description of the research project, objectives, and expected outcomes"
                rows={4}
                value={formData.description}
                onChange={(event) => setFormData({ ...formData, description: event.target.value })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="start">Proposed Start Date</Label>
                <Input
                  id="start"
                  type="date"
                  value={formData.startDate}
                  onChange={(event) => setFormData({ ...formData, startDate: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end">Proposed End Date</Label>
                <Input
                  id="end"
                  type="date"
                  value={formData.endDate}
                  onChange={(event) => setFormData({ ...formData, endDate: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KES">KES - Kenya Shillings</SelectItem>
                    <SelectItem value="USD">USD - US Dollars</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Budget Breakdown</CardTitle>
              <CardDescription>Detail the budget by line item</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addBudgetLine}>
              <Plus className="mr-1 size-3.5" />
              Add Line
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.budgetItems.map((item, index) => (
              <div key={item.id}>
                <div className="mb-2 flex items-center gap-2">
                  <p className="flex-1 text-sm font-medium text-foreground">{item.category}</p>
                  {formData.budgetItems.length > 1 ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      onClick={() => removeBudgetLine(index)}
                    >
                      <Trash2 className="size-3.5 text-muted-foreground" />
                    </Button>
                  ) : null}
                </div>
                <div className="grid gap-3 sm:grid-cols-4">
                  <div className="sm:col-span-2">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(event) => updateBudgetItem(index, 'description', event.target.value)}
                    />
                  </div>
                  <Input
                    type="number"
                    placeholder="Qty"
                    min={0}
                    value={item.quantity || ''}
                    onChange={(event) =>
                      updateBudgetItem(index, 'quantity', parseInt(event.target.value, 10) || 0)
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Unit Cost"
                    min={0}
                    value={item.unitCost || ''}
                    onChange={(event) =>
                      updateBudgetItem(index, 'unitCost', parseFloat(event.target.value) || 0)
                    }
                  />
                </div>
                <p className="mt-1 text-right text-xs text-muted-foreground">
                  Subtotal: {formatCurrency(item.quantity * item.unitCost, formData.currency)}
                </p>
                {index < formData.budgetItems.length - 1 ? <Separator className="mt-3" /> : null}
              </div>
            ))}
            <Separator />
            <div className="flex items-center justify-between">
              <span className="font-semibold text-foreground">Grand Total</span>
              <span className="text-lg font-bold text-foreground">
                {formatCurrency(grandTotal, formData.currency)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">SDG & Strategic Pillar Mapping</CardTitle>
            <CardDescription>
              Map your project to relevant Sustainable Development Goals and JOOUST strategic pillars
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="mb-3 block text-sm font-semibold">Select Relevant SDGs</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {SDG_LIST.map((sdg, index) => {
                  const num = index + 1
                  const selected = formData.sdgMappings.includes(num)
                  return (
                    <button
                      key={num}
                      onClick={() => toggleSDG(num)}
                      className={`rounded-lg border p-2.5 text-left text-xs transition-all ${
                        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <span className={selected ? 'font-bold text-primary' : 'font-bold'}>
                        SDG {num}
                      </span>
                      <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{sdg}</p>
                    </button>
                  )
                })}
              </div>
            </div>
            <div>
              <Label className="mb-3 block text-sm font-semibold">JOOUST Strategic Pillars</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {[
                  'Health Innovation',
                  'Sustainable Development',
                  'Digital Transformation',
                  'Food Security',
                  'Community Engagement',
                  'Climate Action',
                  'Youth Empowerment',
                  'Education Quality',
                ].map((pillar) => {
                  const selected = formData.strategicPillars.includes(pillar)
                  return (
                    <button
                      key={pillar}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          strategicPillars: selected
                            ? prev.strategicPillars.filter((value) => value !== pillar)
                            : [...prev.strategicPillars, pillar],
                        }))
                      }
                      className={`rounded-lg border p-2.5 text-xs transition-all ${
                        selected ? 'border-secondary bg-secondary/20' : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <span className={selected ? 'font-bold' : 'text-muted-foreground'}>
                        {pillar}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Supporting Documents</CardTitle>
            <CardDescription>
              Upload proposal documents, CVs, budget templates, and supporting materials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className="rounded-lg border-2 border-dashed p-10 text-center"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault()
                handleFilesSelected(event.dataTransfer.files)
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 48 48"
                fill="none"
                className="mx-auto mb-3 text-muted-foreground/40"
              >
                <path
                  d="M14 30L24 20L34 30"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M24 20V42"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M40.5 35.2C42.6 33.5 44 30.9 44 28C44 23.6 40.4 20 36 20C35.6 20 35.2 20 34.8 20.1C33.2 14.4 28.1 10 22 10C14.8 10 9 15.8 9 23C9 23.6 9 24.1 9.1 24.6C5.6 25.8 3 29.1 3 33C3 37.4 6.6 41 11 41H39"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <p className="text-sm font-medium text-foreground">Drag and drop files here</p>
              <p className="mt-1 text-xs text-muted-foreground">PDF, DOC, XLS up to 25MB each</p>
              <Button
                variant="outline"
                className="mt-4"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                Browse Files
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(event) => {
                  handleFilesSelected(event.target.files)
                  event.target.value = ''
                }}
              />
            </div>
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="conflict"
                  checked={formData.conflictDeclaration}
                  onCheckedChange={(value) =>
                    setFormData({ ...formData, conflictDeclaration: value === true })
                  }
                />
                <div>
                  <Label htmlFor="conflict" className="cursor-pointer text-sm font-medium text-foreground">
                    Conflict of Interest Declaration
                  </Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    I declare that I have no conflict of interest with regards to this proposal, and all
                    information provided is accurate and complete.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review & Submit</CardTitle>
            <CardDescription>Review all details before submitting for internal review</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Title</p>
                <p className="text-sm font-medium text-foreground">{formData.title || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Type</p>
                <p className="text-sm font-medium text-foreground capitalize">
                  {formData.type.replace('_', ' ')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Department</p>
                <p className="text-sm font-medium text-foreground">
                  {formData.department || 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Currency</p>
                <p className="text-sm font-medium text-foreground">{formData.currency}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Start Date</p>
                <p className="text-sm font-medium text-foreground">
                  {formData.startDate || 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">End Date</p>
                <p className="text-sm font-medium text-foreground">
                  {formData.endDate || 'Not specified'}
                </p>
              </div>
            </div>
            {formData.sdgMappings.length > 0 ? (
              <div>
                <p className="mb-1 text-xs text-muted-foreground">SDG Mapping</p>
                <div className="flex flex-wrap gap-1">
                  {formData.sdgMappings
                    .sort((a, b) => a - b)
                    .map((sdg) => (
                      <span
                        key={sdg}
                        className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground"
                      >
                        SDG {sdg}
                      </span>
                    ))}
                </div>
              </div>
            ) : null}
            <Separator />
            <div>
              <p className="mb-2 text-xs text-muted-foreground">Budget Summary</p>
              {formData.budgetItems
                .filter((item) => item.unitCost > 0)
                .map((item, index) => (
                  <div key={index} className="flex justify-between py-1 text-sm">
                    <span className="text-muted-foreground">
                      {item.category}: {item.description}
                    </span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(item.quantity * item.unitCost, formData.currency)}
                    </span>
                  </div>
                ))}
              <Separator className="my-2" />
              <div className="flex justify-between text-sm font-bold">
                <span className="text-foreground">Grand Total</span>
                <span className="text-foreground">
                  {formatCurrency(grandTotal, formData.currency)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
        >
          <ArrowLeft className="mr-2 size-4" /> Previous
        </Button>
        {step < steps.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} className="bg-primary text-primary-foreground">
            Next <ArrowRight className="ml-2 size-4" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} className="bg-primary text-primary-foreground" disabled={submitting}>
            <Check className="mr-2 size-4" />
            {submitting ? 'Submitting...' : 'Submit Proposal'}
          </Button>
        )}
      </div>
    </div>
  )
}
